import {Color} from '@benchling/nucleus-ui';

/**
 * Number of shades per agent color. Shades tell one agent's chat sessions apart (see
 * `buildTimelineRows`), so this is also how many sessions an agent can show before a shade repeats.
 */
export const SHADES_PER_COLOR = 10;

export interface TimelineAgentColor {
  name: string;
  /** SHADES_PER_COLOR hex values, light to dark. */
  shades: ReadonlyArray<string>;
}

/**
 * The ordered palette of agent colors. An agent takes the color at its index in the foundry roster,
 * modulo this list's length, so its color is stable across reloads and range changes. Hues are ordered so
 * that neighbors in the roster are visually distinct (no two similar hues adjacent). Built from Nucleus
 * Color tokens; yellow and lime are left out because no shade of them is legible on white.
 *
 * Nucleus ramps run 50..600 in steps of 50. 50 and 100 are dropped because they vanish on a white lane;
 * what remains is exactly SHADES_PER_COLOR steps, ordered light to dark. Each token is referenced
 * explicitly (rather than looked up by name) so that unused-token analysis and renames see the usages.
 */
export const TIMELINE_AGENT_PALETTE: ReadonlyArray<TimelineAgentColor> = [
  {
    name: 'blue',
    shades: [
      Color.BLUE_150,
      Color.BLUE_200,
      Color.BLUE_250,
      Color.BLUE_300,
      Color.BLUE_350,
      Color.BLUE_400,
      Color.BLUE_450,
      Color.BLUE_500,
      Color.BLUE_550,
      Color.BLUE_600,
    ],
  },
  {
    name: 'orange',
    shades: [
      Color.ORANGE_150,
      Color.ORANGE_200,
      Color.ORANGE_250,
      Color.ORANGE_300,
      Color.ORANGE_350,
      Color.ORANGE_400,
      Color.ORANGE_450,
      Color.ORANGE_500,
      Color.ORANGE_550,
      Color.ORANGE_600,
    ],
  },
  {
    name: 'grape',
    shades: [
      Color.GRAPE_150,
      Color.GRAPE_200,
      Color.GRAPE_250,
      Color.GRAPE_300,
      Color.GRAPE_350,
      Color.GRAPE_400,
      Color.GRAPE_450,
      Color.GRAPE_500,
      Color.GRAPE_550,
      Color.GRAPE_600,
    ],
  },
  {
    name: 'teal',
    shades: [
      Color.TEAL_150,
      Color.TEAL_200,
      Color.TEAL_250,
      Color.TEAL_300,
      Color.TEAL_350,
      Color.TEAL_400,
      Color.TEAL_450,
      Color.TEAL_500,
      Color.TEAL_550,
      Color.TEAL_600,
    ],
  },
  {
    name: 'red',
    shades: [
      Color.RED_150,
      Color.RED_200,
      Color.RED_250,
      Color.RED_300,
      Color.RED_350,
      Color.RED_400,
      Color.RED_450,
      Color.RED_500,
      Color.RED_550,
      Color.RED_600,
    ],
  },
  {
    name: 'green',
    shades: [
      Color.GREEN_150,
      Color.GREEN_200,
      Color.GREEN_250,
      Color.GREEN_300,
      Color.GREEN_350,
      Color.GREEN_400,
      Color.GREEN_450,
      Color.GREEN_500,
      Color.GREEN_550,
      Color.GREEN_600,
    ],
  },
  {
    name: 'pink',
    shades: [
      Color.PINK_150,
      Color.PINK_200,
      Color.PINK_250,
      Color.PINK_300,
      Color.PINK_350,
      Color.PINK_400,
      Color.PINK_450,
      Color.PINK_500,
      Color.PINK_550,
      Color.PINK_600,
    ],
  },
];
